import { useContext, useEffect } from 'react'
import styled from 'styled-components'

import { MODALS_ID } from '../../../constants'

import { StoresContext } from '../../../provider/StoreProvider'
import { ModalContext } from '../../../provider/ModalProvider'

import { useUserInfo } from '../../../hooks/useUserInfo'

import { Head } from '../../../components/head/Head'
import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { RowBetween, CenterWrap } from '../../../components/common'
import { Header } from '../../../components/header'
import { Text } from '../../../components/text'
import { Spinner } from '../../../components/spinner/Spinner'
import { SettingListContainer } from '../components/SettingListContainer'
import type { SettingListContainerProps } from '../components/SettingListContainer'
import { CheckVerificationModal, CreateAccountModal, LogoutModal } from '../modal'

import { ReactComponent as ArrowGrayIcon } from '../../../assets/ico/common/arrow/right/arrow_right_gray-06_medium.svg'

const HEAD_INFO = {
  title: '개인 정보 관리 | 미술품 조각투자 플랫폼, 아티피오',
  ogTitle: '개인 정보 관리 | 미술품 조각투자 플랫폼, 아티피오',
  ogUrl: window.location.href,
}

export function PersonalInfo() {
  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)
  const { openModal } = useContext(ModalContext)

  const { data: userInfo, isPending, isError, isLogin, isCheckingLogin } = useUserInfo()

  useEffect(() => {
    setShowTab(false)
  }, [])

  useEffect(() => {
    if (isPending) return
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isPending, isLogin, isCheckingLogin])

  if (!userInfo || isPending || isError) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <Header type="SUB" title="개인 정보 관리" href="/setting" />
          <CenterWrap>
            <Spinner />
          </CenterWrap>
        </Wrap>
      </>
    )
  }

  const logout = () => openModal({ id: MODALS_ID.LOGOUT, Component: LogoutModal })

  const PERSONAL_LIST: SettingListContainerProps['items'] = [
    {
      list: [
        {
          title: '휴대폰 번호',
          rightContent: (
            <RowBetween>
              <Text size={14} color="text_secondary" style={{ marginRight: '8px' }}>
                {userInfo?.identification ? (
                  <>
                    {userInfo?.identification?.danalPhoneNumber?.substring(0, 3)}****
                    {userInfo?.identification?.danalPhoneNumber?.substring(7, 11)}
                  </>
                ) : null}
              </Text>
              <ArrowGrayIcon />
            </RowBetween>
          ),
          link: '/personalInfo/phone',
        },
        {
          title: '이메일',
          link: '/email',
          rightContent: <ArrowGrayIcon />,
        },
        {
          title: '자택 주소',
          link: '/address',
          rightContent: <ArrowGrayIcon />,
        },
        {
          title: '출금 계좌',
          link: '/withdrawalAccount',
          rightContent: <ArrowGrayIcon />,
          onClick: (e) => {
            const stopEvent = () => {
              e?.preventDefault()
              e?.stopPropagation()
            }

            if (!userInfo) {
              stopEvent()
              return
            }

            const { identification, withdrawalAccount } = userInfo

            if (!identification) {
              stopEvent()
              openModal({ id: MODALS_ID.CHECK_VERIFICATION, Component: CheckVerificationModal })
              return
            }

            if (withdrawalAccount) return

            stopEvent()
            openModal({ id: MODALS_ID.CREATE_ACCOUNT, Component: CreateAccountModal })
          },
        },
        {
          title: '예치금 충전 계좌',
          link: '/depositAccount',
          rightContent: <ArrowGrayIcon />,
          onClick: (e) => {
            const stopEvent = () => {
              e?.preventDefault()
              e?.stopPropagation()
            }

            if (!userInfo) {
              stopEvent()
              return
            }

            const { identification, depositAccount } = userInfo

            if (!identification) {
              stopEvent()
              openModal({ id: MODALS_ID.CHECK_VERIFICATION, Component: CheckVerificationModal })
              return
            }

            if (depositAccount) return

            stopEvent()
            openModal({ id: MODALS_ID.CREATE_ACCOUNT, Component: CreateAccountModal })
          },
        },
        {
          title: '로그아웃',
          onClick: logout,
        },
        {
          title: (
            <Text size={18} weight={500} color="gray_4">
              회원탈퇴
            </Text>
          ),
          link: '/withdrawalUser',
        },
      ],
    },
  ]

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="개인 정보 관리" href="/setting" />
        <Content>
          <Container>
            <SettingListContainer items={PERSONAL_LIST} itemGap={8} />
          </Container>
        </Content>
      </Wrap>
    </>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 16px 0;
`
